import React, { useState, useCallback, useRef } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import update from 'immutability-helper';

const type = 'DragableBodyRow';

const DragableBodyRow = ({
  index,
  moveRow,
  className,
  style,
  ...restProps
}) => {
  const ref = useRef();
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName:
          dragIndex < index ? ' drop-over-downward' : ' drop-over-upward'
      };
    },
    drop: (item) => {
      if (moveRow) {
        moveRow(item.index, index);
      }
    }
  });
  const [, drag] = useDrag({
    type,
    item: { type, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });
  drop(drag(ref));

  return (
    <tr
      ref={ref}
      className={`${className}${isOver ? dropClassName : ''}`}
      style={{ cursor: 'move', ...style }}
      {...restProps}
    />
  );
};

const DragSortingTable = ({
  children,
  components = {},
  dataSource = [],
  filter,
  onSorting,
  ...rest
}) => {
  const getDataSource = (filterInverse) => {
    if (filter && filterInverse) {
      return dataSource.filter((c) => !filter(c));
    }

    if (filter) {
      return dataSource.filter(filter);
    }

    return dataSource;
  };

  if (components) {
    components.body = { row: DragableBodyRow, ...components.body };
  } else {
    components = { body: { row: DragableBodyRow, ...components.body } };
  }

  const moveRow = useCallback(
    (dragIndex, hoverIndex) => {
      const dragRow = getDataSource()[dragIndex];

      if (onSorting) {
        const rejecteds = filter ? getDataSource(true) : [];

        onSorting([
          ...update(getDataSource(), {
            $splice: [
              [dragIndex, 1],
              [hoverIndex, 0, dragRow]
            ]
          }),
          ...rejecteds
        ]);
      }
    },
    [dataSource]
  );

  return (
    <DndProvider backend={HTML5Backend}>
      {React.cloneElement(children, {
        onRow: (_, index) => ({
          index,
          moveRow
        }),
        moveRow,
        components,
        dataSource,
        filter,
        ...rest
      })}
    </DndProvider>
  );
};

export default DragSortingTable;
